/**
 * Price calculation utilities with platform and credit card fees
 *
 * Fee structure:
 * - Platform fee: 2% of base price, capped at $30
 * - Credit card fee: Gross-up for Stripe processing (2.9% + $0.30)
 */

/** The platform fee percentage shown on the payment pages ("Platform Fee (2%)") */
export const PLATFORM_FEE_PERCENT = 2.0;

/**
 * The fee formula every checkout uses. No free-price guard: callers decide what $0 means
 * (a $0 amount still gets the $0.30 Stripe fixed fee here: total 0.31).
 * The backend trusts these numbers (SPO-1241), so a change here changes what customers pay.
 * @param amount - The amount before fees
 * @param options.roundBase - Round amount + platform fee to cents before the gross-up
 *   (the wallet top-up did this; for amounts in whole cents it gives the same result)
 */
export function calculateCardFees(amount: number, { roundBase = false } = {}) {
  // Platform fee: 2% of the amount, capped at $30
  let convenienceFee = parseFloat((0.02 * amount).toFixed(2));
  if (convenienceFee > 30) {
    convenienceFee = 30;
  }

  let totalWithConvenienceFee = amount + convenienceFee;
  if (roundBase) {
    totalWithConvenienceFee = parseFloat(totalWithConvenienceFee.toFixed(2));
  }

  // Credit card fee: gross-up for Stripe 2.9% + $0.30
  // ([Amount] + [Convenience Fee] + [0.30 stripe fixed fee]) / (1 - [2.9 stripe % fee])
  const total = parseFloat(((totalWithConvenienceFee + 0.30) / (1 - 0.029)).toFixed(2));
  const creditCardFee = parseFloat((total - totalWithConvenienceFee).toFixed(2));

  return {
    convenienceFee,
    totalWithConvenienceFee,
    creditCardFee,
    total,
  };
}

/**
 * Fees as the sticker purchase and wallet deposit pages send them to create a payment intent:
 * totalFee and totalCost are summed from the rounded fees, not taken from the formula's total.
 * @param amount - The amount before fees
 */
export function calculatePaymentIntentFees(amount: number) {
  const { convenienceFee, creditCardFee } = calculateCardFees(amount);
  const totalFee = parseFloat((convenienceFee + creditCardFee).toFixed(2));
  const totalCost = parseFloat((amount + totalFee).toFixed(2));
  return {
    convenienceFee, creditCardFee, totalFee, totalCost,
  };
}

/**
 * Fees for the cart checkout (createCartCheckoutIntent): an empty or free cart has no fees.
 * @param subtotal - The cart subtotal before fees
 */
export function calculateCartFees(subtotal: number) {
  if (subtotal === 0) return { convenienceFee: 0, creditCardFee: 0 };
  const { convenienceFee, creditCardFee } = calculateCardFees(subtotal);
  return { convenienceFee, creditCardFee };
}

export interface WalletTopUpCoupon {
  percent_off?: number | null;
  amount_off?: number | null;
}

/**
 * Fees for a wallet top-up (InitiateWalletPaymentIntent), with the optional marketplace upgrade
 * (one more $0.30 Stripe fixed fee, grossed up) and Stripe coupon.
 * total is not rounded; the page rounds it for display and for the intent.
 * The coupon values keep the types the page always had: couponDiscount and withCouponNetAmount
 * are strings from toFixed in some branches.
 * @param amount - The top-up amount before fees
 */
export function calculateWalletTopUpFees(
  amount: number,
  { marketplaceUpgrade = false, coupon = null as WalletTopUpCoupon | null } = {},
) {
  const {
    convenienceFee, totalWithConvenienceFee, total: feeTotal,
  } = calculateCardFees(amount, { roundBase: true });

  let total = feeTotal;
  // if marketplace upgrade is enabled, add extra 0.30 (stripe fixed fee) to the total amount
  if (marketplaceUpgrade) {
    total += 0.30 / 0.971;
  }
  const creditCardFee = parseFloat((total - totalWithConvenienceFee).toFixed(2));

  let couponResult: {
    couponDiscount: number | string;
    withCouponStripeFee: number;
    withCouponNetAmount: string;
  } | null = null;
  if (coupon) {
    let couponDiscount: number | string;
    if (coupon.percent_off != null && coupon.percent_off !== 0) {
      couponDiscount = Math.round(coupon.percent_off * total) / 100;
    } else {
      // A coupon without amount_off gives "NaN", as before
      couponDiscount = ((coupon.amount_off as number) / 100).toFixed(2);
    }
    total -= Number(couponDiscount);
    const withCouponStripeFee = parseFloat((0.3 + (0.029 * total)).toFixed(2));
    const withCouponNetAmount = (total - withCouponStripeFee).toFixed(2);
    couponResult = { couponDiscount, withCouponStripeFee, withCouponNetAmount };
  }

  return {
    convenienceFee,
    creditCardFee,
    total,
    coupon: couponResult,
  };
}

/**
 * Calculate the total price including all fees
 * @param basePrice - The base price before fees
 * @returns The total price including platform and credit card fees
 */
export function calculatePriceWithFees(basePrice: number): number {
  if (basePrice <= 0) return 0;
  return calculateCardFees(basePrice).total;
}

/**
 * Format a single price with fees as just the amount (no label)
 * @param basePrice - The base price before fees
 * @returns Formatted string like "$52.83" or "Free"
 */
export function formatPriceAmountWithFees(basePrice: number): string {
  if (basePrice <= 0) return 'Free';

  const totalWithFees = calculatePriceWithFees(basePrice);
  return `$${totalWithFees.toFixed(2)}`;
}

/**
 * Format a single price with fees as a display string (inline format)
 * @param basePrice - The base price before fees
 * @returns Formatted string like "$52.83 incl. fees" or "Free"
 */
export function formatPriceWithFees(basePrice: number): string {
  if (basePrice <= 0) return 'Free';

  return `${formatPriceAmountWithFees(basePrice)} incl. fees`;
}

/**
 * Format a price range with fees as just the amount (no label)
 * @param minPrice - The minimum base price
 * @param maxPrice - The maximum base price
 * @returns Formatted string like "$27.13-$80.63" or "Free"
 */
export function formatPriceRangeAmountWithFees(minPrice: number, maxPrice: number): string {
  if (minPrice <= 0 && maxPrice <= 0) return 'Free';

  const minWithFees = calculatePriceWithFees(minPrice);
  const maxWithFees = calculatePriceWithFees(maxPrice);

  if (minWithFees === maxWithFees) {
    return minWithFees === 0 ? 'Free' : `$${minWithFees.toFixed(2)}`;
  }

  if (minPrice <= 0) {
    return `$0-$${maxWithFees.toFixed(2)}`;
  }

  return `$${minWithFees.toFixed(2)}-$${maxWithFees.toFixed(2)}`;
}

/**
 * Format a price range with fees as a display string (inline format)
 * @param minPrice - The minimum base price
 * @param maxPrice - The maximum base price
 * @returns Formatted string like "$27.13-$80.63 incl. fees" or "Free"
 */
export function formatPriceRangeWithFees(minPrice: number, maxPrice: number): string {
  if (minPrice <= 0 && maxPrice <= 0) return 'Free';

  const amount = formatPriceRangeAmountWithFees(minPrice, maxPrice);
  return amount === 'Free' ? 'Free' : `${amount} incl. fees`;
}

/**
 * Format an add-on price (with + prefix) as just the amount (no label)
 * @param basePrice - The base price before fees
 * @returns Formatted string like "+$10.28" or "Free"
 */
export function formatAddonPriceAmountWithFees(basePrice: number): string {
  if (basePrice <= 0) return 'Free';

  const totalWithFees = calculatePriceWithFees(basePrice);
  return `+$${totalWithFees.toFixed(2)}`;
}

/**
 * Format an add-on price (with + prefix) including fees (inline format)
 * @param basePrice - The base price before fees
 * @returns Formatted string like "+$10.28 incl. fees"
 */
export function formatAddonPriceWithFees(basePrice: number): string {
  if (basePrice <= 0) return 'Free';

  return `${formatAddonPriceAmountWithFees(basePrice)} incl. fees`;
}

/**
 * Get breakdown of fees for a given base price
 * @param basePrice - The base price before fees
 * @returns Object with platformFee, creditCardFee, and total
 */
export function getFeeBreakdown(basePrice: number) {
  if (basePrice <= 0) {
    return {
      platformFee: 0,
      creditCardFee: 0,
      total: 0,
    };
  }

  const {
    convenienceFee: platformFee, creditCardFee, total,
  } = calculateCardFees(basePrice);

  return {
    platformFee,
    creditCardFee,
    total,
  };
}

/**
 * Get the minimum base unit cost for a sticker (before fees).
 * Used for shop cart subtotal and add-to-cart pricing.
 */
export function getStickerMinimumUnitCost(sticker: any): number {
  if (!sticker) return 0;

  if (sticker.campaignType === 'Ticket' && sticker.priceRange) {
    const rangeMatch = String(sticker.priceRange).match(
      /\$?(\d+(?:\.\d+)?)\s*[-–]\s*\$?(\d+(?:\.\d+)?)/,
    );
    if (rangeMatch) {
      return parseFloat(rangeMatch[1]);
    }
    const singleMatch = String(sticker.priceRange).match(/\$?(\d+(?:\.\d+)?)/);
    if (singleMatch) {
      return parseFloat(singleMatch[1]);
    }
  }

  if (sticker.campaignType === 'Ticket' && sticker.varients?.length > 0) {
    const costs = sticker.varients
      .map((variant: any) => Number(variant.cost))
      .filter((cost: number) => !Number.isNaN(cost) && cost > 0);
    if (costs.length > 0) {
      return Math.min(...costs);
    }
  }

  if (
    sticker.campaignType === 'Sweepstakes'
    && sticker.sweepstakesSticker?.entryCost?.length > 0
  ) {
    const costs = sticker.sweepstakesSticker.entryCost
      .map((cost: number) => Number(cost))
      .filter((cost: number) => !Number.isNaN(cost) && cost >= 0);
    if (costs.length > 0) {
      return Math.min(...costs);
    }
  }

  if (Array.isArray(sticker.quickCosts) && sticker.quickCosts.length > 0) {
    const costs = sticker.quickCosts
      .map((cost: number) => Number(cost))
      .filter((cost: number) => !Number.isNaN(cost) && cost > 0);
    if (costs.length > 0) {
      return Math.min(...costs);
    }
  }

  if (Number(sticker.cost) > 0) {
    return Number(sticker.cost);
  }

  return 0;
}
